import React, { useState } from 'react';
import { Check, HelpCircle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PMSFAQ from './PMSFAQ';
import BackButton from '../BackButton';

const PMSPricing = () => {
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Define the feature rows to ensure consistency with tooltip content
  const featureRows = [
    { 
      label: "Fee structure", 
      type: "text",
      tooltip: "The overall approach to how fees are charged for portfolio management services."
    },
    { 
      label: "Management fee", 
      type: "text",
      tooltip: "Annual charge calculated as a percentage of your assets under management, regardless of portfolio performance."
    },
    { 
      label: "Performance fee", 
      type: "text",
      tooltip: "Additional fee calculated as a percentage of profits generated above a specified hurdle rate or benchmark."
    },
    { 
      label: "Minimum investment", 
      type: "text",
      tooltip: "The minimum amount required to start investing in our portfolio management service."
    },
    { 
      label: "Dedicated portfolio manager", 
      type: "check",
      tooltip: "A personal investment professional assigned specifically to your account who will oversee your portfolio and be your direct point of contact."
    },
    { 
      label: "Customized portfolio strategy", 
      type: "check",
      tooltip: "A tailored investment approach designed specifically for your financial goals, risk tolerance, and time horizon."
    },
    { 
      label: "Monthly portfolio review", 
      type: "check",
      tooltip: "Regular assessment of your portfolio performance and composition to ensure alignment with your investment objectives."
    },
    { 
      label: "Risk management", 
      type: "check",
      tooltip: "Proactive strategies to identify, assess, and mitigate potential investment risks in your portfolio."
    },
    { 
      label: "Tax-efficient investing", 
      type: "check",
      tooltip: "Strategic investment decisions designed to minimize tax liabilities and optimize after-tax returns."
    },
    { 
      label: "Quarterly performance reports", 
      type: "check",
      tooltip: "Comprehensive reports provided every three months detailing your portfolio performance, asset allocation, and investment returns."
    },
    { 
      label: "Research insights access", 
      type: "check",
      tooltip: "Access to proprietary research, market analysis, and investment insights from our expert team."
    },
    { 
      label: "Corporate action handling", 
      type: "check",
      tooltip: "Management of dividends, stock splits, mergers, acquisitions, and other corporate events affecting your portfolio holdings."
    },
    { 
      label: "Real-time portfolio tracking", 
      type: "check",
      tooltip: "24/7 access to monitor your investments, performance, and portfolio composition through our secure online portal."
    },
  ];

  // Define the plan data
  const plans = [
    {
      name: "Performance-Based",
      headline: "0%",
      subtext: "management fee",
      buttonText: "Coming Soon",
      isPrimary: false,
      description: "We earn only when you profit above the threshold",
      values: [
        "Pure performance-based",
        "0% per annum",
        "25% of profits above hurdle rate",
        "₹50 lakhs",
        true, true, true, true, true, true, true, true, true
      ]
    },
    {
      name: "Hybrid",
      headline: "1%",
      subtext: "+ performance fee",
      buttonText: "Schedule Meeting",
      isPrimary: true,
      description: "Balanced approach with lower fixed fee plus performance incentive",
      values: [
        "Combined fee structure",
        "1% per annum",
        "25% profits above hurdle rate",
        "₹50 lakhs",
        true, true, true, true, true, true, true, true, true
      ]
    },
    {
      name: "Fixed Fee",
      headline: "2.25%",
      subtext: "per annum",
      buttonText: "Schedule Meeting",
      isPrimary: false,
      description: "Predictable fixed fee structure with no additional charges",
      values: [
        "Fixed management fee",
        "2.25% per annum",
        "No performance fee",
        "₹50 lakhs",
        true, true, true, true, true, true, true, true, true
      ]
    }
  ];

  // Handle tooltip toggle
  const toggleTooltip = (index) => {
    if (activeTooltip === index) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(index);
    }
  };

  return (
    <>
      <Header />
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 pt-16">
          <BackButton to="/fee" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-2 sm:mb-4 pt-12">Choose Your PMS Fee Structure</h2>
          <p className="text-gray-600 text-sm sm:text-base">Select the portfolio management fee model that aligns with your investment goals</p>
        </div>

        {/* Pricing Table with horizontal scroll for small/medium screens */}
        <div className="relative mb-8 sm:mb-12 max-w-6xl mx-auto">
          {/* Scroll indicator for small/medium screens */}
          <div className="md:hidden text-xs text-gray-500 text-center mb-2 animate-pulse">
            ← Scroll horizontally to view all details →
          </div>
          
          {/* Table container with horizontal scroll */}
          <div className="overflow-x-auto pb-4">
            {/* Minimum width to ensure proper display on small screens */}
            <div className="min-w-[900px] border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 sm:p-4 text-left w-1/4 border border-gray-200">
                      <h3 className="text-base sm:text-xl font-bold text-blue-900">Benefits</h3>
                    </th>
                    {plans.map((plan, index) => (
                      <th key={index} className={`p-3 sm:p-4 w-1/4 border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}>
                        <h3 className="text-lg sm:text-xl font-bold text-blue-900">{plan.name}</h3>
                        <div className="my-3 sm:my-4">
                          <span className="text-2xl sm:text-3xl font-bold text-blue-900">{plan.headline}</span>
                          <span className="text-gray-600 text-sm sm:text-base"> {plan.subtext}</span>
                        </div>
                        {plan.buttonText === 'Schedule Meeting' ? (
                          <a
                            href="/meeting"
                            className={`w-full inline-block text-center py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition-colors text-sm sm:text-base ${
                              plan.isPrimary 
                                ? 'bg-blue-900 text-white hover:bg-blue-800' 
                                : 'border border-blue-900 text-blue-900 hover:bg-blue-50'
                            }`}
                          >
                            {plan.buttonText}
                          </a>
                        ) : (
                          <button
                            className={`w-full py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition-colors text-sm sm:text-base ${
                              plan.isPrimary 
                                ? 'bg-blue-900 text-white hover:bg-blue-800' 
                                : 'border border-blue-900 text-blue-900 hover:bg-blue-50'
                            }`}
                            disabled={plan.buttonText === 'Coming Soon'}
                          >
                            {plan.buttonText}
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 sm:p-4 border border-gray-200 text-sm sm:text-base relative">
                        <div className="flex items-center">
                          <button 
                            className="mr-2 text-gray-400 hover:text-blue-600 focus:outline-none"
                            onClick={() => toggleTooltip(rowIndex)}
                            onMouseEnter={() => setActiveTooltip(rowIndex)}
                            onMouseLeave={() => setActiveTooltip(null)}
                            aria-label={`Information about ${row.label}`}
                          >
                            <HelpCircle className="h-4 w-4" />
                          </button>
                          <span>{row.label}</span>
                          
                          {/* Tooltip content - positioned to the left */}
                          {activeTooltip === rowIndex && (
                            <div className="absolute z-10 left-8 -mt-2 w-64 sm:w-72 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-xs sm:text-sm text-gray-700">
                              {row.tooltip}
                            </div>
                          )}
                        </div>
                      </td>
                      {plans.map((plan, planIndex) => (
                        <td 
                          key={planIndex} 
                          className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}
                        >
                          {row.type === "check" ? (
                            plan.values[rowIndex] ? <Check className="text-green-600 h-4 w-4 sm:h-5 sm:w-5 mx-auto" /> : null
                          ) : (
                            <span className="text-sm sm:text-base">{plan.values[rowIndex]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-3 sm:p-4 border border-gray-200"></td>
                    {plans.map((plan, index) => (
                      <td key={index} className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}>
                        <div className="text-xs sm:text-sm text-gray-600">{plan.description}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Scroll fade indicator on right edge for small screens */}
          <div className="md:hidden absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
        </div>

        <div className="max-w-6xl mx-auto">
          <PMSFAQ />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PMSPricing;